import {
  ICombinationalLogic,
  IFiniteStateMachine,
  IFixedSchedule,
  ILogicalConfiguration,
  IRelativeSchedule,
  IStateTransition,
  TRoutine,
} from 'components/general/types/cdh';

export type TRoutineBlock = TRoutine | ILogicalConfiguration | IStateTransition;
export type TState = {
  blocks: { [key: string]: TRoutineBlock }; // All blocks in the state, keyed by id
  dirtyBlocks: string[]; // Blocks that have been modified (and not deleted)
  deletedBlocks: string[]; // Blocks that have been deleted
};

// Delete a block from the state
export const deleteFromState = (id: string, state: TState) => {
  delete state.blocks[id];
  if (state.dirtyBlocks.includes(id)) {
    state.dirtyBlocks = state.dirtyBlocks.filter((dirtyId) => dirtyId !== id);
  }
  if (!id.startsWith('$temp-')) {
    state.deletedBlocks = [...state.deletedBlocks, id];
  }
};

// Add a block to the state (or overwrite it if it already exists) and mark it as dirty
export const addBlockToState = (block: TRoutineBlock, state: TState) => {
  state.blocks[block.id] = block;
  setDirtyBlock(block.id, state);
};

// Set a block as dirty
export const setDirtyBlock = (id: string, state: TState) => {
  if (!state.dirtyBlocks.includes(id)) {
    state.dirtyBlocks = [...state.dirtyBlocks, id];
  }
};

// Disconnect a logical configuration and delete it if it has no remaining parents
// If parent is provided, only disconnect from that parent
// If configOnly is true, only the configuration is disconnected and parentRoutine/subroutines relationships are not modified
export const disconnectLogicalConfiguration = (
  state: TState,
  id: string,
  options?: { parent?: string; configOnly?: boolean }
) => {
  const { parent, configOnly } = options || {};
  const config = { ...state.blocks[id] } as ILogicalConfiguration;
  const disconnect = (parentId: string) => {
    const parentRoutine = state.blocks[parentId] as ICombinationalLogic;
    state.blocks[parentId] = {
      ...parentRoutine,
      subroutines: parentRoutine.subroutines.filter((id) => id !== config.routine),
      logicalConfigurations: parentRoutine.logicalConfigurations.filter((id) => id !== config.id),
    };
    state.blocks[config.routine] = {
      ...state.blocks[config.routine],
      parentRoutines: (state.blocks[config.routine] as TRoutine).parentRoutines.filter(
        (id) => id !== parentId
      ),
    };
    setDirtyBlock(parentId, state);
  };
  if (parent) {
    !configOnly && disconnect(parent);
    config.combinationalLogicRoutines = config.combinationalLogicRoutines.filter(
      (id) => id !== parent
    );
  } else {
    !configOnly && config.combinationalLogicRoutines.forEach(disconnect);
    config.combinationalLogicRoutines = [];
  }
  !configOnly && setDirtyBlock(config.routine, state);
  if (config.combinationalLogicRoutines.length === 0) {
    deleteFromState(id, state);
  } else {
    state.blocks[id] = config;
    setDirtyBlock(id, state);
  }
};

// Disconnect a state transition by removing it from all FSM routines and deleting it
export const disconnectStateTransition = (state: TState, id: string) => {
  const transition = { ...state.blocks[id] } as IStateTransition;
  const from = state.blocks[transition.fromState] as TRoutine;
  const to = state.blocks[transition.toState] as TRoutine;
  from.parentRoutines.forEach((parent) => {
    if (
      state.blocks[parent].type === 'FiniteStateMachine' &&
      (state.blocks[parent] as IFiniteStateMachine).transitions.includes(transition.id)
    ) {
      state.blocks[parent] = {
        ...state.blocks[parent],
        transitions: (state.blocks[parent] as IFiniteStateMachine).transitions.filter(
          (id) => id !== transition.id
        ),
      };
      setDirtyBlock(parent, state);
    }
  });
  to.parentRoutines.forEach((parent) => {
    if (
      state.blocks[parent].type === 'FiniteStateMachine' &&
      (state.blocks[parent] as IFiniteStateMachine).transitions.includes(transition.id)
    ) {
      state.blocks[parent] = {
        ...state.blocks[parent],
        transitions: (state.blocks[parent] as IFiniteStateMachine).transitions.filter(
          (id) => id !== transition.id
        ),
      };
      setDirtyBlock(parent, state);
    }
  });
  deleteFromState(id, state);
};

// Disconnect a subroutine from a FSM routine, deleting all transitions that reference it
export const disconnectFsmSubroutine = (state: TState, parent: string, child: string) => {
  const fsm = state.blocks[parent] as IFiniteStateMachine;
  const deletedTransitions: string[] = [];
  const newTransitions: string[] = [];
  fsm.transitions.forEach((id) => {
    const transition = state.blocks[id] as IStateTransition;
    if (transition.fromState === child || transition.toState === child) {
      deletedTransitions.push(id);
    } else {
      newTransitions.push(id);
    }
  });
  state.blocks[parent] = {
    ...fsm,
    activeSubroutine: fsm.activeSubroutine === child ? undefined : fsm.activeSubroutine,
    subroutines: fsm.subroutines.filter((id) => id !== child),
    transitions: newTransitions,
  };
  deletedTransitions.forEach((id) => deleteFromState(id, state));
  state.blocks[child] = {
    ...state.blocks[child],
    parentRoutines: (state.blocks[child] as TRoutine).parentRoutines.filter((id) => id !== parent),
  };
  setDirtyBlock(child, state);
  setDirtyBlock(parent, state);
};

// Disconnect a routine from a fixed or relative schedule, deleting all schedule data that references it
export const disconnectSchedule = (state: TState, parent: string, child: string) => {
  const parentRoutine = state.blocks[parent] as IFixedSchedule | IRelativeSchedule;
  const { [child]: oldData, ...newScheduleData } = parentRoutine.scheduleData;
  state.blocks[parent] = {
    ...parentRoutine,
    defaultSubroutine:
      parentRoutine.defaultSubroutine === child ? undefined : parentRoutine.defaultSubroutine,
    subroutines: parentRoutine.subroutines.filter((id) => id !== child),
    scheduleData: newScheduleData,
  };
  state.blocks[child] = {
    ...state.blocks[child],
    parentRoutines: (state.blocks[child] as TRoutine).parentRoutines.filter((id) => id !== parent),
  };
  setDirtyBlock(child, state);
  setDirtyBlock(parent, state);
};
